import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { GenericForm, GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { uploadTaskAttachments, deleteTaskAttachment } from '@/features/project-management/todos/store';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';

interface FileAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

interface Project {
  id: string;
  name: string;
}

interface User {
  id: string;
  full_name: string;
  company_name?: string | null;
  email?: string;
}

interface TaskComment {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  audience?: string;
  author: {
    id: string;
    display_name?: string | null;
  };
}

interface Task {
  id: string;
  title: string;
  description: string;
  project_id: string;
  assignee_id: string;
  status: string;
  priority: string;
  due_date: string;
  due_time: string;
  reminder_times: string | null;
}

interface TodoFormProps {
  mode: 'create' | 'edit';
  initialData?: Task;
  projects: Project[];
  users: User[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  session: any;
  comments?: TaskComment[];
  onAddComment?: (content: string, audience?: string) => void;
  onDeleteComment?: (id: string) => void;
  attachments?: FileAttachment[];
  onDeleteAttachment?: (id: string) => void;
}

const normalizeDueTime = (t: string | null | undefined) => {
  if (!t) return null;
  return t.slice(0, 5);
};

export function TodoForm({ 
  mode, 
  initialData, 
  projects, 
  users, 
  onSubmit, 
  onCancel,
  comments = [],
  onAddComment,
  onDeleteComment,
  attachments = [],
  onDeleteAttachment
}: TodoFormProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const uploadTaskAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<FileAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadTaskAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteTaskAttachmentFn = useCallback((id: string) => deleteTaskAttachment(id, user?.id), [user]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemoveSelectedFile,
    handleDelete: handleDeleteFileBase,
  } = useAttachmentStaging<FileAttachment>({
    entityId: initialData?.id,
    uploadFn: uploadTaskAttachmentsFn,
    deleteFn: deleteTaskAttachmentFn,
    initialUploadedFiles: attachments,
  });
  const handleDeleteFile = useCallback(async (attachmentId: string) => {
    const ok = await handleDeleteFileBase(attachmentId);
    if (ok) {
      onDeleteAttachment?.(attachmentId);
    }
  }, [handleDeleteFileBase, onDeleteAttachment]);
  const [localComments, setLocalComments] = useState<TaskComment[]>(comments);
  const [dueDate, setDueDate] = useState<string | null>(initialData?.due_date || null);
  const [dueTime, setDueTime] = useState<string | null>(normalizeDueTime(initialData?.due_time) || null);
  const [reminderTimes, setReminderTimes] = useState<string | null>(initialData?.reminder_times || null);

  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = new Date();
        time.setHours(hour, minute, 0);
        const formattedTime = time.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit', 
          hour12: true 
        });
        options.push({
          label: formattedTime,
          value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    return options;
  }, []);

  const initialValues = useMemo(() => {
    const normalizedTime = normalizeDueTime(initialData?.due_time);
    return {
      title: initialData?.title || '',
      description: initialData?.description || '',
      project_id: initialData?.project_id || getDefaultProjectIdForNewRecord(selectedProject),
      assignee_id: initialData?.assignee_id || '',
      status: initialData?.status || 'pending',
      priority: initialData?.priority || 'medium',
      due_date: initialData?.due_date || '',
      due_time: normalizedTime || '',
      reminder_times: initialData?.reminder_times || 'no-reminder',
    };
  }, [initialData, selectedProject]);

  const todoFields = useMemo<GenericFormField[]>(() => {
    const fields: GenericFormField[] = [
      {
        name: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        row: 1,
        placeholder: 'Enter To-Do Title'
      },
      {
        name: 'project_id',
        label: 'Project',
        type: 'select',
        required: true,
        row: 1,
        options: projects.map(p => ({ label: p.name, value: p.id }))
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        row: 2,
        colSpan: 2,
        placeholder: 'Enter To-Do description'
      },
      {
        name: 'assignee_id',
        label: 'Assignee',
        type: 'select',
        row: 3,
        colSpan: 0.5,
        placeholder: 'Select',
        options: users.map(u => ({ label: u.company_name || u.full_name || u.email || '', value: u.id })),
        membershipProjectField: 'project_id'
      },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        row: 3,
        colSpan: 0.5,
        options: [
          { label: 'Pending', value: 'pending' },
          { label: 'In Progress', value: 'in_progress' },
          { label: 'Completed', value: 'completed' },
          { label: 'Cancelled', value: 'cancelled' }
        ]
      },
      {
        name: 'priority',
        label: 'Priority',
        type: 'select',
        required: true,
        row: 3,
        colSpan: 0.5,
        options: [
          { label: 'Low', value: 'low' },
          { label: 'Medium', value: 'medium' },
          { label: 'High', value: 'high' }
        ]
      },
      {
        name: 'due_date',
        label: 'Due Date',
        type: 'date',
        row: 4,
        colSpan: 0.5
      },
      {
        name: 'due_time',
        label: 'Due Time',
        type: 'select',
        row: 4,
        colSpan: 0.5,
        placeholder: 'Select',
        disabled: !dueDate,
        options: timeOptions
      },
      {
        name: 'reminder_times',
        label: 'Reminder',
        type: 'select',
        row: 4,
        colSpan: 0.5,
        disabled: !dueTime,
        options: [
          { label: 'No reminder', value: 'no-reminder' },
          { label: '15 minutes before', value: '15' },
          { label: '30 minutes before', value: '30' },
          { label: '1 hour before', value: '60' },
          { label: '2 hours before', value: '120' },
          { label: '8 hours before', value: '480' },
          { label: '1 day before', value: '1440' },
          { label: '2 days before', value: '2880' }
        ]
      }
    ];
    return fields;
  }, [projects, users, timeOptions, dueDate, dueTime]);

  const handleFieldChange = (name: string, value: any, formData: Record<string, any>) => {
    if (name === 'due_date') {
      const cleared = !value;
      setDueDate(cleared ? null : value);
      if (cleared) {
        // Due Time (and therefore Reminder) can't exist without a Due
        // Date -- clearing the date clears whatever time/reminder was
        // set, instead of leaving an orphaned time the field's own
        // `disabled` state would then hide from the user entirely.
        setDueTime(null);
        setReminderTimes(null);
      }
    } else if (name === 'due_time') {
      const cleared = !value;
      setDueTime(cleared ? null : value);
      if (cleared) {
        setReminderTimes(null);
      }
    } else if (name === 'reminder_times') {
      setReminderTimes(value === 'no-reminder' ? null : value);
    }
  };

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (data: Record<string, any>) => {
    if (!data.title || !data.title.trim()) {
      setValidationError('Please enter a title before saving.');
      return;
    }
    if (!data.project_id) {
      setValidationError('Please select a project before saving.');
      return;
    }
    if (data.due_time && !data.due_date) {
      setValidationError('Please select a due date before setting a due time.');
      return;
    }
    setValidationError(null);

    onSubmit({
      ...data,
      title: data.title.trim(),
      description: data.description.trim(),
      project_id: data.project_id,
      assignee_id: data.assignee_id || null,
      status: data.status,
      priority: data.priority,
      due_date: data.due_date || null,
      due_time: data.due_time || null,
      reminder_times: data.reminder_times === 'no-reminder' || !data.reminder_times ? null : data.reminder_times,
      attachmentsFiles: selectedFiles
    });
  };


  const handleAddComment = async (content: string, audience?: string) => {
    if (!content.trim() || !initialData?.id || !user?.id) return;
    try {
      if (onAddComment) {
        await onAddComment(content.trim(), audience);
      }
    } catch (error) {
      console.error('[TodoForm] add comment failed', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (onDeleteComment) {
      await onDeleteComment(commentId);
    }
  };

  useEffect(() => {
    if (initialData?.id) {
      setUploadedFiles(attachments || []);
    }
  }, [initialData?.id, attachments, setUploadedFiles]);

  useEffect(() => {
    setLocalComments(comments || []);
  }, [comments]);

  // Browse Drive follows the form's own Project field
  const attachmentsSection = (projectId: string) => (
    <GenericAttachmentsSection
      projectId={projectId}
      attachments={uploadedFiles.map(file => ({
        id: file.id,
        file_name: file.file_name,
        file_type: file.file_type,
        file_size: file.file_size,
        file_url: file.file_url,
        created_at: file.created_at,
        created_by: file.created_by,
        created_by_user: file.created_by_user
      }))}
      pendingFiles={selectedFiles}
      onUpload={handleFileUpload}
      onDelete={handleDeleteFile}
      onRemovePending={handleRemoveSelectedFile}
      mode={mode}
    />
  );

  const commentsSection = (
    <GenericCommentsSection
      comments={localComments.map(comment => ({
        id: comment.id,
        content: comment.content,
        created_at: comment.created_at,
        created_by: comment.created_by,
        audience: comment.audience,
        author: comment.author
      }))}
      onAddComment={handleAddComment}
      onDeleteComment={handleDeleteComment}
      mode={mode}
      audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
      placeholder={mode === 'create' ? "Comments are available after creating the to-do" : "Add a comment..."}
    />
  );

  return (
    <GenericForm
      title="To-Do"
      mode={mode}
      fields={todoFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isOpen={true}
      onFieldChange={handleFieldChange}
      error={validationError}
      additionalSections={(values) => (
        <>
          {attachmentsSection(values.project_id || '')}
          {commentsSection}
        </>
      )}
      submitButtonText="Save Changes"
    />
  );
}
